import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import { Camera, Activity, MapPin, FolderOpen, ExternalLink, X } from 'lucide-react'
import * as Dialog from '@radix-ui/react-dialog'
import { Button } from '@/components/ui/button'
import type { CameraMapPin } from '@/types'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useQuery } from '@tanstack/react-query'
import { camerasApi, eventsApi } from '@/api/endpoints'
import { queryKeys, queryOptions } from '@/config/queryConfig'
import { cn } from '@/lib/utils'
import { toast } from 'sonner'
import { useDashboardMapAnchor } from '@/contexts/DashboardMapAnchorContext'
import { useDirectionStore } from '@/store/directionStore'
import { CommandHome } from '@/pages/dashboard/CommandHome'
import { SaasHome } from '@/pages/dashboard/SaasHome'
import { ExpressiveHome } from '@/pages/dashboard/ExpressiveHome'
import { PinDetailDialog } from '@/pages/dashboard/PinDetailDialog'

// Relative time — uses i18n translation keys
function useRelativeTime() {
  const { t } = useTranslation('common')
  return (dateStr: string): string => {
    const diff = Date.now() - new Date(dateStr).getTime()
    const s = Math.floor(diff / 1000)
    if (s < 60) return t('time.justNow')
    const m = Math.floor(s / 60)
    if (m < 60) return t('time.minutesAgo', { count: m })
    const h = Math.floor(m / 60)
    if (h < 24) return t('time.hoursAgo', { count: h })
    const d = Math.floor(h / 24)
    return t('time.daysAgo', { count: d })
  }
}

// Animates a number from its previous value to `target` with ease-out cubic.
// On first render (initialLoad = true) it counts up from 0.
// On subsequent poll updates it counts from the previous displayed value.
function useCountUp(target: number, duration = 700, enabled = true) {
  const [value, setValue] = useState(target)
  const from = useRef(target)
  const raf = useRef<number>()

  useEffect(() => {
    if (raf.current) cancelAnimationFrame(raf.current)
    if (!enabled) { setValue(target); from.current = target; return }
    const start = from.current
    if (start === target) return
    const delta = target - start
    const began = performance.now()
    const animate = (now: number) => {
      const progress = Math.min((now - began) / duration, 1)
      const eased = 1 - Math.pow(1 - progress, 3)
      setValue(Math.round(start + eased * delta))
      if (progress < 1) {
        raf.current = requestAnimationFrame(animate)
      } else {
        from.current = target
      }
    }
    raf.current = requestAnimationFrame(animate)
    return () => { if (raf.current) cancelAnimationFrame(raf.current) }
  }, [target, duration, enabled])

  return value
}

interface StatCardProps {
  title: string
  value: string | number
  description: string
  icon: React.ReactNode
  accent: string
  iconColor: string
  topBorder: string
  loading?: boolean
  delay?: string
}

function StatCard({ title, value, description, icon, accent, iconColor, topBorder, loading, delay }: StatCardProps) {
  const numericValue = typeof value === 'number' ? value : (!isNaN(Number(value)) ? Number(value) : null)
  const counted = useCountUp(numericValue ?? 0, 700, !loading && numericValue !== null)
  const displayValue = loading ? null : (numericValue !== null ? counted : value)

  return (
    <Card className={cn('animate-fade-up overflow-hidden', delay)}>
      <div className={cn('h-0.5 w-full', topBorder)} />
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 pt-4">
        <CardTitle className="text-body font-medium text-muted-foreground">{title}</CardTitle>
        <div className={cn('flex h-8 w-8 items-center justify-center rounded-lg', accent, iconColor)}>
          {icon}
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <>
            <div className="shimmer h-8 w-16 rounded mb-1" />
            <div className="shimmer h-3 w-24 rounded" />
          </>
        ) : (
          <>
            <div className="text-heading">{displayValue}</div>
            <p className="text-label text-muted-foreground mt-0.5">{description}</p>
          </>
        )}
      </CardContent>
    </Card>
  )
}

export function DashboardPage() {
  const { t } = useTranslation('cameras')
  const { t: te } = useTranslation('events')
  const { t: tc } = useTranslation('common')
  const relativeTime = useRelativeTime()
  const navigate = useNavigate()
  const [selectedPin, setSelectedPin] = useState<CameraMapPin | null>(null)
  const direction = useDirectionStore(s => s.direction)

  // Persistent map anchor — publishes a DOM ref so PersistentDashboardMap
  // (mounted once at MainLayout level) can portal <MapView> into it.
  const { setAnchor, setOnMarkerClick } = useDashboardMapAnchor()

  const { data: camerasPage, isLoading: camerasLoading } = useQuery({
    queryKey: queryKeys.cameras.summary(),
    queryFn: () => camerasApi.getPaginated(1),
    ...queryOptions.cameras.all,
  })

  const cameras = camerasPage?.results ?? []
  const totalCameras = camerasPage?.count ?? 0

  const { data: eventsData, isLoading: eventsLoading } = useQuery({
    queryKey: queryKeys.events.recent(),
    queryFn: () => eventsApi.getAll({}, 1),
    ...queryOptions.events.recent,
  })

  const totalEvents = eventsData?.count ?? 0

  // Lightweight map pins — separate dedicated endpoint, no pagination.
  // Uses the same query key as PersistentDashboardMap so React Query
  // deduplicates the network request (one HTTP call shared).
  const { data: mapPins = [] } = useQuery({
    queryKey: queryKeys.cameras.mapPins(),
    queryFn: () => camerasApi.getMapPins(),
    staleTime: 60_000,
  })

  // Register the onMarkerClick handler with the persistent map context.
  // We keep mapPins in a ref so the handler closure always reads the latest
  // list without re-subscribing the handler on every mapPins reference change.
  // (Earlier version put mapPins in deps — but the default `mapPins = []`
  // returns a fresh array reference on every pre-load render, which caused
  // a setState-in-useEffect infinite loop. The ref pattern fixes that.)
  const mapPinsRef = useRef(mapPins)
  useEffect(() => {
    mapPinsRef.current = mapPins
  }, [mapPins])

  useEffect(() => {
    setOnMarkerClick((id: number) => {
      const pin = mapPinsRef.current.find(p => p.id === id)
      if (pin) setSelectedPin(pin)
    })
    return () => setOnMarkerClick(null)
  }, [setOnMarkerClick])

  // Clear the anchor on unmount so PersistentDashboardMap renders null while
  // the user is on other pages (portal target gone, MapView instance preserved).
  useEffect(() => {
    return () => setAnchor(null)
  }, [setAnchor])

  const DELAYS = ['delay-75', 'delay-150', 'delay-225', 'delay-300', 'delay-375']

  // ── Per-direction variants ─────────────────────────────────────────────────
  // All hooks above run identically for every direction. Only the returned
  // JSX differs. Variants receive props derived from the already-fetched data;
  // they NEVER issue their own queries.

  const sharedProps = {
    cameras,
    totalCameras,
    camerasLoading,
    events: eventsData?.results ?? [],
    totalEvents,
    eventsLoading,
    mapPins,
    setAnchor,
  }

  if (direction === 'command') return (
    <>
      <CommandHome {...sharedProps} />
      <PinDetailDialog pin={selectedPin} onClose={() => setSelectedPin(null)} />
    </>
  )
  if (direction === 'saas') return (
    <>
      <SaasHome {...sharedProps} />
      <PinDetailDialog pin={selectedPin} onClose={() => setSelectedPin(null)} />
    </>
  )
  if (direction === 'expressive') return (
    <>
      <ExpressiveHome
        {...sharedProps}
        onEventClick={(id) => navigate(`/events?openEvent=${id}`)}
      />
      <PinDetailDialog pin={selectedPin} onClose={() => setSelectedPin(null)} />
    </>
  )

  return (
    <div className="space-y-4 sm:space-y-6">

      {/* Stats Row — 3 compact metric cards */}
      <div className="grid gap-3 grid-cols-2 sm:grid-cols-3">
        <StatCard
          title={t('dashboard.totalCameras')}
          value={totalCameras}
          description={t('dashboard.registered', { count: totalCameras })}
          icon={<Camera className="h-4 w-4" />}
          accent="bg-primary/10"
          iconColor="text-primary"
          topBorder="bg-primary"
          loading={camerasLoading}
          delay="delay-75"
        />
        <StatCard
          title={t('dashboard.detectionEvents')}
          value={totalEvents}
          description={t('dashboard.allTimeDetections')}
          icon={<Activity className="h-4 w-4" />}
          accent="bg-amber-500/10"
          iconColor="text-amber-600 dark:text-amber-400"
          topBorder="bg-amber-500"
          loading={eventsLoading}
          delay="delay-150"
        />
        <StatCard
          title={t('dashboard.activeCameras')}
          value={camerasLoading ? 0 : cameras.filter(c => c.is_active).length}
          description={camerasLoading ? tc('status.loading') : t('dashboard.total', { count: totalCameras })}
          icon={<Camera className="h-4 w-4" />}
          accent="bg-emerald-500/10"
          iconColor="text-emerald-600 dark:text-emerald-400"
          topBorder="bg-emerald-500"
          loading={camerasLoading}
          delay="delay-225"
        />
      </div>

      {/* Main panel — Recent Events (left, 2/5) + Camera Map (right, 3/5)
          Height: viewport minus header, padding, stats row, gaps, footer */}
      <div className="grid gap-4 lg:grid-cols-5 animate-fade-up delay-300 min-w-0
        lg:h-[calc(100dvh-22rem)]">

        {/* Recent Events — 3/5 on desktop, full width on mobile */}
        <Card className="lg:col-span-3 min-w-0 overflow-hidden flex flex-col h-[400px] lg:h-full">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Activity className="h-4 w-4 text-muted-foreground" />
                <CardTitle className="text-sm font-semibold">{te('title')}</CardTitle>
              </div>
              <div className="flex items-center gap-3">
                {eventsData?.count !== undefined && eventsData.count > 0 && (
                  <span className="text-xs text-muted-foreground">
                    {eventsData.count.toLocaleString()} {te('total')}
                  </span>
                )}
                <Link
                  to="/events"
                  className="text-xs font-medium text-primary hover:text-primary/80 transition-colors"
                >
                  {te('viewAll', { defaultValue: 'View all →' })}
                </Link>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0 flex-1 overflow-hidden">
            <div className="h-full overflow-y-auto px-4 pb-4">
              {eventsLoading ? (
                <div className="space-y-2">
                  {[...Array(8)].map((_, i) => (
                    <div key={i} className="flex items-center gap-3 rounded-lg p-2">
                      <div className="shimmer h-8 w-8 rounded-lg flex-shrink-0" />
                      <div className="flex-1 space-y-1.5">
                        <div className="shimmer h-3 w-28 rounded" />
                        <div className="shimmer h-2.5 w-20 rounded" />
                      </div>
                      <div className="shimmer h-2.5 w-12 rounded" />
                    </div>
                  ))}
                </div>
              ) : eventsData?.results && eventsData.results.length > 0 ? (
                <div className="space-y-0.5">
                  {eventsData.results.slice(0, 20).map((event, i) => (
                    <div
                      key={event.id}
                      className={cn(
                        'flex items-center gap-2.5 rounded-lg px-2 py-2',
                        'hover:bg-muted/60 transition-colors duration-150 cursor-default',
                        'animate-fade-up',
                        DELAYS[Math.min(i, DELAYS.length - 1)],
                      )}
                    >
                      {/* Icon */}
                      <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-amber-500/10">
                        <Activity className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" />
                      </div>

                      {/* Event info */}
                      <div className="flex-1 min-w-0">
                        <p className="text-body font-medium leading-tight truncate">
                          {tc(`eventTypes.${event.event_type}`, { defaultValue: event.event_type.replace(/_/g, ' ') })}
                        </p>
                        <div className="flex items-center gap-1 mt-0.5">
                          <Camera className="h-3 w-3 text-muted-foreground/60 flex-shrink-0" />
                          <p className="text-label text-muted-foreground leading-tight truncate">
                            {event.camera_name}
                          </p>
                          {event.detected_objects && event.detected_objects.length > 0 && (
                            <div className="flex gap-1 flex-shrink-0 ml-1">
                              {event.detected_objects.slice(0, 2).map((obj) => (
                                <span
                                  key={obj}
                                  className="inline-flex items-center rounded-full bg-primary/10 px-1.5 py-0 text-[10px] font-medium text-primary"
                                >
                                  {tc(`detectionClasses.${obj}`, { defaultValue: obj.replace(/_/g, ' ') })}
                                </span>
                              ))}
                              {event.detected_objects.length > 2 && (
                                <span className="text-[10px] text-muted-foreground">
                                  +{event.detected_objects.length - 2}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                        {event.camera_group && (
                          <div className="flex items-center gap-1 mt-0.5">
                            <FolderOpen className="h-3 w-3 text-muted-foreground/60 flex-shrink-0" />
                            <p className="text-[10px] text-muted-foreground/60 leading-tight truncate">{event.camera_group}</p>
                          </div>
                        )}
                      </div>

                      {/* Timestamp */}
                      <span
                        className="text-caption text-muted-foreground whitespace-nowrap ml-1 flex-shrink-0"
                        title={new Date(event.occurred_at).toLocaleString()}
                      >
                        {relativeTime(event.occurred_at)}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="h-full flex flex-col items-center justify-center gap-1 text-center">
                  <Activity className="h-6 w-6 text-muted-foreground/40" />
                  <p className="text-sm text-muted-foreground">{te('noRecentEvents')}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Camera Map — 2/5 on desktop, full width on mobile */}
        <Card className="lg:col-span-2 border-border/60 overflow-hidden min-w-0 flex flex-col h-[350px] lg:h-full">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-muted-foreground" />
              <CardTitle className="text-sm font-semibold">{t('map.title')}</CardTitle>
              {mapPins.length > 0 && (
                <span className="ml-auto text-xs text-muted-foreground">
                  {t('map.mapped', { count: mapPins.length })}
                </span>
              )}
            </div>
          </CardHeader>
          <CardContent className="p-0 flex-1 overflow-hidden">
            {mapPins.length > 0 ? (
              // Anchor div: PersistentDashboardMap portals <MapView> into this node.
              // Using `ref={setAnchor}` as a callback ref so React calls setAnchor
              // with the DOM element on mount and null on unmount automatically.
              // The explicit useEffect cleanup above handles the unmount case too,
              // ensuring the anchor is cleared when DashboardPage unmounts.
              <div
                ref={setAnchor}
                className="h-full min-h-0 w-full overflow-hidden rounded-b-xl"
              />
            ) : (
              <div className="h-full min-h-0 flex flex-col items-center justify-center gap-2 text-center">
                <MapPin className="h-8 w-8 text-muted-foreground/20" />
                <p className="text-sm text-muted-foreground">{t('map.noCoordinates')}</p>
                <p className="text-xs text-muted-foreground/60">{t('map.addCoordinatesHint')}</p>
                <Link
                  to="/cameras"
                  className="mt-2 text-xs font-medium text-primary hover:text-primary/80 transition-colors"
                >
                  {t('map.goToCameras', { defaultValue: 'Go to Cameras →' })}
                </Link>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Camera Pin Detail Modal */}
      {selectedPin && (() => {
        const lat = parseFloat(selectedPin.latitude)
        const lng = parseFloat(selectedPin.longitude)
        const googleMapsUrl = `https://www.google.com/maps?q=${lat},${lng}`
        return (
          <Dialog.Root open={!!selectedPin} onOpenChange={(open) => { if (!open) setSelectedPin(null) }}>
            <Dialog.Portal>
              <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" style={{ animationDuration: '200ms' }} />
              <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-[calc(100vw-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-xl border border-border bg-background shadow-2xl overflow-hidden data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=open]:zoom-in-[0.98] data-[state=closed]:zoom-out-[0.98] data-[state=open]:slide-in-from-bottom-1 data-[state=closed]:slide-out-to-bottom-1" style={{ animationDuration: '250ms', animationTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)' }}>
                <Dialog.Title className="sr-only">{selectedPin.name}</Dialog.Title>

                {/* Header */}
                <div className="flex items-center justify-between px-5 py-4 border-b border-border/60">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 flex-shrink-0">
                      <Camera className="h-4 w-4 text-primary" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold truncate">{selectedPin.name}</p>
                      {selectedPin.location && (
                        <div className="flex items-center gap-1 mt-0.5">
                          <MapPin className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                          <p className="text-xs text-muted-foreground truncate">{selectedPin.location}</p>
                        </div>
                      )}
                    </div>
                  </div>
                  <Dialog.Close asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8 flex-shrink-0">
                      <X className="h-4 w-4" />
                    </Button>
                  </Dialog.Close>
                </div>

                {/* Details */}
                <div className="px-5 py-4 space-y-3">
                  {/* Group */}
                  {selectedPin.group_names.length > 0 && (
                    <div className="flex items-center gap-2">
                      <FolderOpen className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                      <p className="text-sm text-muted-foreground">{selectedPin.group_names.join(', ')}</p>
                    </div>
                  )}

                  {/* Coordinates */}
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    <p className="text-sm font-mono text-muted-foreground">{lat.toFixed(6)}, {lng.toFixed(6)}</p>
                  </div>

                  {/* Last Event */}
                  {selectedPin.last_event_type && selectedPin.last_event_at && (
                    <div className="flex items-center gap-2">
                      <Activity className="h-4 w-4 text-amber-500 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium">
                          {tc(`eventTypes.${selectedPin.last_event_type}`, { defaultValue: selectedPin.last_event_type.replace(/_/g, ' ') })}
                        </p>
                        <p className="text-xs text-muted-foreground">{relativeTime(selectedPin.last_event_at)}</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex gap-2 px-5 py-4 border-t border-border/60 bg-muted/20">
                  <Button
                    variant="default"
                    size="sm"
                    className="flex-1 h-9 text-xs gap-1.5"
                    onClick={() => {
                      if (selectedPin.is_locked) {  // #077 — SIM mismatch: locked
                        toast.error('Camera locked', { description: 'SIM mismatch — under review.' })
                        return
                      }
                      setSelectedPin(null); navigate(`/events?camera=${selectedPin.id}`)
                    }}
                  >
                    <Activity className="h-3.5 w-3.5" />
                    {te('viewAll')}
                  </Button>
                  <a href={googleMapsUrl} target="_blank" rel="noopener noreferrer" className="flex-1">
                    <Button variant="outline" size="sm" className="w-full h-9 text-xs gap-1.5">
                      <ExternalLink className="h-3.5 w-3.5" />
                      {t('map.openInMaps')}
                    </Button>
                  </a>
                </div>
              </Dialog.Content>
            </Dialog.Portal>
          </Dialog.Root>
        )
      })()}
    </div>
  )
}
